import { z } from 'zod';
export const AUTOMATION_CATEGORY_NAME_MAX_LENGTH = 32;
export const AUTOMATION_CATEGORIES_PER_AUTOMATION_MAX = 10;
export const AUTOMATION_CATEGORY_DEFAULT_COLOR = '#6B7280';
export const AUTOMATION_CATEGORY_PRESET_COLORS = [
    '#2563EB',
    '#16A34A',
    '#0D9488',
    '#7C3AED',
    '#DC2626',
    '#D97706',
    '#DB2777',
    '#EA580C',
    AUTOMATION_CATEGORY_DEFAULT_COLOR,
];
export const automationCategoryNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(AUTOMATION_CATEGORY_NAME_MAX_LENGTH)
    .describe('Display name. Trimmed; unique per partner regardless of case.');
export const automationCategoryColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a hex color like #1D4ED8')
    .describe('Hex color (#RRGGBB) used for the category pill');
export const automationCategoryResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    color: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const listAutomationCategoriesResponseSchema = z.object({
    results: z.array(automationCategoryResponseSchema),
});
export const createAutomationCategoryRequestSchema = z.object({
    name: automationCategoryNameSchema,
    color: automationCategoryColorSchema.optional(),
});
export const updateAutomationCategoryRequestSchema = z
    .object({
    name: automationCategoryNameSchema.optional(),
    color: automationCategoryColorSchema.optional(),
})
    .refine(update => Object.keys(update).length > 0, {
    message: 'At least one field must be provided',
});
export const automationCategoryUsageResponseSchema = z.object({
    templateCount: z.number().int().describe('Partner automation templates using the category'),
    tenantAutomationCount: z
        .number()
        .int()
        .describe('Tenant custom automations using the category; deleting removes it from them'),
    tenantCount: z.number().int().describe('Distinct tenants with a custom automation using the category'),
});
export const setAutomationTemplateCategoriesRequestSchema = z.object({
    categoryIds: z
        .array(z.string().uuid())
        .max(AUTOMATION_CATEGORIES_PER_AUTOMATION_MAX)
        .describe('Replaces every category on the template. Pass an empty array to clear them.'),
});
export const automationTemplateCategoriesResponseSchema = z.object({
    templateId: z.string().uuid(),
    categories: z.array(automationCategoryResponseSchema),
});
export var AutomationCategoryScope;
(function (AutomationCategoryScope) {
    AutomationCategoryScope["MANAGED"] = "managed";
    AutomationCategoryScope["CUSTOM"] = "custom";
})(AutomationCategoryScope || (AutomationCategoryScope = {}));
export const tenantAutomationCategoryResponseSchema = automationCategoryResponseSchema.extend({
    scope: z
        .nativeEnum(AutomationCategoryScope)
        .describe("'managed' categories belong to the partner; 'custom' ones belong to this tenant"),
});
export const listTenantAutomationCategoriesQuerySchema = z.object({
    inUse: z
        .enum(['true', 'false'])
        .transform(value => value === 'true')
        .optional()
        .describe('Only return categories used on at least one of the tenant’s automations'),
});
export const listTenantAutomationCategoriesResponseSchema = z.object({
    results: z.array(tenantAutomationCategoryResponseSchema),
});
export const setAutomationCategoriesRequestSchema = z.object({
    categoryIds: z
        .array(z.string().uuid())
        .max(AUTOMATION_CATEGORIES_PER_AUTOMATION_MAX)
        .describe("Replaces every category on the custom automation. Accepts the partner's categories and this tenant's own. Pass an empty array to clear them."),
});
export const automationCategoriesResponseSchema = z.object({
    automationId: z.string().uuid(),
    categories: z.array(tenantAutomationCategoryResponseSchema),
});
export const automationCategoryIdsQuerySchema = z
    .string()
    .transform(value => value.split(',').map(categoryId => categoryId.trim()))
    .pipe(z.array(z.string().uuid()))
    .describe('Comma-separated category IDs. Returns automations with any of them.');
//# sourceMappingURL=automation-category.js.map